import { FiFilter, FiRotateCcw, FiSearch } from "react-icons/fi";
import { Children } from 'react';
import SearchableSelect from '../../../components/reports/SearchableSelect';

function FilterSelect({ label, value, onChange, children }) {
  return (
    <label className="block min-w-0">
      <span className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
        {label}
      </span>
      <div className="mt-1.5"><SearchableSelect closeOnScroll viewportTop={80} value={value === 'All' ? '' : value} onChange={next => onChange(next || 'All')} placeholder={`All ${label.toLowerCase()}`} options={Children.toArray(children).filter(child => child.props.value !== 'All').map(child => ({ value:child.props.value,label:child.props.children }))} /></div>
    </label>
  );
}

export default function EmployeeFilters({
  searchTerm,
  setSearchTerm,
  department,
  setDepartment,
  branch,
  setBranch,
  status,
  setStatus,
  bankDetails,
  setBankDetails,
  identityDetails,
  setIdentityDetails,
  systemAccess,
  setSystemAccess,
  departments = ["All"],
  branches = ["All"],
}) {
  const statuses = ["All", "Active", "Draft", "Inactive", "Exited"];
  const hasActiveFilters = [
    searchTerm,
    department !== "All",
    branch !== "All",
    status !== "All",
    bankDetails !== "All",
    identityDetails !== "All",
    systemAccess !== "All",
  ].some(Boolean);

  const resetFilters = () => {
    setSearchTerm("");
    setDepartment("All");
    setBranch("All");
    setStatus("All");
    setBankDetails("All");
    setIdentityDetails("All");
    setSystemAccess("All");
  };

  return (
    <section className="relative z-10 mb-6 overflow-visible rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 rounded-t-2xl border-b border-gray-100 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-800 text-white shadow-sm">
            <FiFilter size={18} />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Find employees</h2>
            <p className="text-sm text-gray-500">
              Search people, assignments, access, and missing records.
            </p>
          </div>
        </div>

        {hasActiveFilters ? (
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-primary-200 bg-white px-3.5 py-2 text-sm font-semibold text-primary-800 transition hover:bg-primary-50"
          >
            <FiRotateCcw size={15} />
            Reset filters
          </button>
        ) : null}
      </div>

      <div className="p-5">
        <div className="relative">
          <FiSearch
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-primary-800"
            size={19}
          />
          <input
            type="search"
            placeholder="Search name, nickname, employee code, email, phone, department, designation, or branch"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3.5 pl-12 pr-4 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-primary-800 focus:bg-white focus:ring-4 focus:ring-primary-100"
          />
        </div>

        <div className="mt-5 grid gap-x-4 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
          <FilterSelect label="Branch" value={branch} onChange={setBranch}>
            {branches.map((branchName) => (
              <option key={branchName} value={branchName}>
                {branchName === "All" ? "All branches" : branchName}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label="Department"
            value={department}
            onChange={setDepartment}
          >
            {departments.map((departmentName) => (
              <option key={departmentName} value={departmentName}>
                {departmentName === "All" ? "All departments" : departmentName}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect label="Employment status" value={status} onChange={setStatus}>
            {statuses.map((item) => (
              <option key={item} value={item}>
                {item === "All" ? "All statuses" : item}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label="Bank details"
            value={bankDetails}
            onChange={setBankDetails}
          >
            <option value="All">All bank detail states</option>
            <option value="Complete">Bank details complete</option>
            <option value="Missing">Bank details missing</option>
          </FilterSelect>

          <FilterSelect
            label="Identity documents"
            value={identityDetails}
            onChange={setIdentityDetails}
          >
            <option value="All">All identity detail states</option>
            <option value="Complete">Aadhaar and PAN complete</option>
            <option value="Missing">Identity details missing</option>
            <option value="Missing Aadhaar">Aadhaar missing</option>
            <option value="Missing PAN">PAN missing</option>
          </FilterSelect>

          <FilterSelect
            label="System access"
            value={systemAccess}
            onChange={setSystemAccess}
          >
            <option value="All">All access levels</option>
            <option value="ERP Access">ERP access</option>
            <option value="Self-service only">Self-service only</option>
          </FilterSelect>
        </div>
      </div>
    </section>
  );
}
